// Pure search + facet rules for the Test Data Landscape's subset list.
//
// Semantics: an empty facet means "all"; values WITHIN a facet are OR'd; the facets and the free
// text query are AND'd together. `connById` is `AppContext.connections` keyed by String(id) — a
// subset source carries only its dataSourceId/name, so the database a source lives in comes from
// the connection record (falling back to the source's own name when the connection is unknown).
import { engineKey, isMaskedSubset, parseSeeds } from '../tdmUtils';

export const DATA_FACETS = ['MASKED', 'RAW'];

const lower = (v) => String(v == null ? '' : v).toLowerCase();
// An IMPORTED subset has no source rows; its subset.json described them by name and engine.
const sourcesOf = (subset) => (subset && (subset.sources && subset.sources.length ? subset.sources : subset.importedSources)) || [];
const connOf = (src, connById) => (connById && src ? connById[String(src.dataSourceId)] : null) || null;

/** The database a subset source reads from: the connection's `database`, else the source name. */
export const sourceDatabase = (src, connById) => {
  const conn = connOf(src, connById);
  const db = conn && conn.database != null ? String(conn.database).trim() : '';
  return db || String((src && src.dataSourceName) || '').trim();
};

/** Sorted, de-duplicated engine keys and database names across every subset's sources. */
export const extractFacets = (subsets, connById) => {
  const engines = new Set();
  const databases = new Set();
  (subsets || []).forEach((s) => sourcesOf(s).forEach((src) => {
    const e = engineKey(src.engine);
    if (e) engines.add(e);
    const db = sourceDatabase(src, connById);
    if (db) databases.add(db);
  }));
  const sort = (set) => Array.from(set).sort((a, b) => a.localeCompare(b));
  return { engines: sort(engines), databases: sort(databases) };
};

/** Case-insensitive substring over name, description, source names, start table and where clause. */
export const matchesQuery = (subset, q) => {
  const needle = lower(q).trim();
  if (!needle) return true;
  const sel = (subset && subset.selection) || {};
  const hay = [
    subset && subset.name,
    subset && subset.description,
    ...sourcesOf(subset).map((src) => src.dataSourceName),
    sel.startTable,
    sel.where,
  ];
  return hay.some((v) => lower(v).includes(needle));
};

/** `data` ⊆ ['MASKED', 'RAW']; MASKED = every masking mode but NONE. */
export const matchesData = (subset, data) => {
  if (!data || data.length === 0) return true;
  const masked = isMaskedSubset(subset);
  return data.some((d) => (String(d).toUpperCase() === 'MASKED' ? masked : !masked));
};

/** Any source whose engine (aliased) is one of the picked engines. */
export const matchesEngines = (subset, engines) => {
  if (!engines || engines.length === 0) return true;
  const picked = engines.map(engineKey);
  return sourcesOf(subset).some((src) => picked.includes(engineKey(src.engine)));
};

/** Any source whose database is one of the picked names. */
export const matchesDatabases = (subset, databases, connById) => {
  if (!databases || databases.length === 0) return true;
  return sourcesOf(subset).some((src) => databases.includes(sourceDatabase(src, connById)));
};

/** Seed-id filter: SEEDS mode and any pasted id (string-compared) among the subset's seeds. */
export const matchesKeyIds = (subset, keyIds) => {
  if (!keyIds || keyIds.length === 0) return true;
  const sel = (subset && subset.selection) || {};
  if (String(sel.mode || '').toUpperCase() !== 'SEEDS') return false;
  const seeds = (sel.seeds || []).map((s) => String(s));
  return keyIds.some((id) => seeds.includes(String(id)));
};

/**
 * `filters = { query, data, engines, databases, keyIds }` — `keyIds` is the already-parsed array
 * (the Landscape runs `parseSeeds` on its textarea); a `keyIdsText` string is accepted as well.
 */
export const filterSubsets = (subsets, filters, connById) => {
  const f = filters || {};
  const keyIds = f.keyIds || (f.keyIdsText != null ? parseSeeds(f.keyIdsText) : []);
  return (subsets || []).filter((s) => matchesQuery(s, f.query)
    && matchesData(s, f.data)
    && matchesEngines(s, f.engines)
    && matchesDatabases(s, f.databases, connById)
    && matchesKeyIds(s, keyIds));
};

/** How many facets carry a value — the "Filters · N" count on the accordion toggle. */
export const activeFacetCount = (filters) => {
  const f = filters || {};
  let n = 0;
  if (f.data && f.data.length) n += 1;
  if (f.engines && f.engines.length) n += 1;
  if (f.databases && f.databases.length) n += 1;
  if (parseSeeds(f.keyIdsText).length) n += 1;
  return n;
};
